import LazyImage from "@Components/Image";
import config from "@Config/index";
import CloseIcon from "@Icons/CloseIcon";
import GoldStarIcon from "@Icons/GoldStarIcon";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import {
  APP_BANNER_TEXT,
  APP_DOWNLOAD_URL_MOBILE_BANNER,
  APP_DOWNLOADS,
  APP_REVIEW,
  PROFILE_DROPDOWN,
} from "constants/configConstants";
import getAppLink from "@Utils/getAppLink";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import setCookie from "helpers/setCookie";
import useTranslationHook from "hooks/useTranslationHook";
import { useEffect } from "react";
import classes from "./banner.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useCashBackValue from "hooks/useCashBackValue";
import useCustomWindowOpen from "@Components/CustomWindowOpen";
import {
  APP_PROMOTION_BANNER_VIEW_TRANSITION_NAME,
  isPdpBackViewTransitionActive,
  isPdpViewTransitionSupported,
} from "@Utils/pdpViewTransitionNav";

export type AppPromotionBannerProps = {
  from: string;
  onClose?: () => void;
  addSticky?: boolean;
};

const AppPromotionBanner = ({ from, onClose, addSticky }: AppPromotionBannerProps) => {
  const customWindowOpen = useCustomWindowOpen();
  const onProfilePage = from === PROFILE_DROPDOWN;
  const { t } = useTranslationHook();
  const transAppBannerText = t(APP_BANNER_TEXT);

  useEffect(() => {
    const eventEmitterTimeout = setTimeout(() => {
      gtagEventEmitter({
        action: "app_download_option_shown",
        category: "app_download_option",
        value: from,
      });
    }, 2000);

    return () => clearTimeout(eventEmitterTimeout);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const appRedirectionHandler = () => {
    gtagEventEmitter({
      action: "app_download_click",
      category: "app_download_option",
      value: from,
    });
    customWindowOpen({
      url: getAppLink(APP_DOWNLOAD_URL_MOBILE_BANNER[from]),
      target: "_blank",
      isExternal: true,
    });
  };

  const handleClose = () => {
    gtagEventEmitter({
      action: "app_download_banner_dismissed",
      category: "app_download_option",
      value: from,
    });
    setCookie({
      cname: "showAppBanner",
      cvalue: "false",
      exdays: 90,
    });
    if (onClose) onClose();
  };
  const cashbackValue = useCashBackValue({ page: from }).displayValue;
  const bannerTransitionStyle =
    isPdpViewTransitionSupported() && !isPdpBackViewTransitionActive()
      ? ({ viewTransitionName: APP_PROMOTION_BANNER_VIEW_TRANSITION_NAME } as React.CSSProperties)
      : undefined;

  return (
    <div
      className={formatClassNames(classes.appWrapper, addSticky && classes.sticky)}
      style={bannerTransitionStyle}
    >
      {!onProfilePage && (
        <div
          data-testid="topnav-download-app-close-button"
          onClick={handleClose}
          className={classes.iconWrapper}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              handleClose();
            }
          }}
          aria-label="close"
        >
          <CloseIcon size={12} />
        </div>
      )}
      <div
        className={classes.wrapper}
        style={{
          padding: onProfilePage ? "12px 6px" : "16px 16px 16px 0px",
        }}
      >
        <LazyImage
          src={
            config.IS_CHINA_APP
              ? `${config.IMAGE_STATIC_URL}/images/amber-app-logo.svg`
              : `${config.IMAGE_STATIC_ASSETS_COMPONENTS_PATH}/AppPromotionBanner/assets/amber-app-logo.png`
          }
          alt="amber-app"
          height="42px"
          width="42px"
          isNotLazy
        />
        <div className={classes.textWrapper}>
          <div data-testid="topnav-download-app-cash-back-message" className={classes.text}>
            {cashbackValue} {transAppBannerText}
          </div>
          <div data-testid="topnav-download-app-ratings-and-downloads" className={classes.subtext}>
            <GoldStarIcon />
            <span className={classes.app_rating}> {APP_REVIEW}</span>
            {" | "}
            {t(APP_DOWNLOADS)}
          </div>
        </div>
        <div onClick={appRedirectionHandler} className={classes.ctaWrap} role="none">
          <button data-testid="topnav-download-app-link" type="button" className={classes.cta}>
            {t("searchpage.topsearchsection.appromotionbanner")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default wrapperHOC(AppPromotionBanner, {
  componentName: "AppPromotionBanner",
  showForChina: true,
  showForWhiteLable: false,
  showForApp: false,
});
